/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { useViewedScreen } from "@packages/analytics";
import type { EmailAuthFlowForm } from "@packages/sdk";
import {
  emailAuthFlowSchema,
  useRequestAuth,
  useRequestGeolocation,
  useRequestOtp,
} from "@packages/sdk";
import { HallowDataTestId } from "@tools/playwright/global";
import { useForm } from "react-hook-form";

import { spacing } from "../../../../global/emotion";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { useValidator } from "../../../hooks/useValidator.emotion";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import { standardSchemaResolver } from "../../../reactHookForm";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { useAlert } from "../../_base/modals/Dialog/Dialog.emotion";
import { FormField } from "../forms/wrapper/FormField.emotion";
import { AuthError } from "./AuthError.emotion";
import { EmailHeader } from "./AuthFlowHeader.emotion";
import { AuthForm } from "./AuthForm.emotion";
import { AuthSubmitButton } from "./AuthSubmitButton.emotion";
import { AuthTextInput } from "./AuthTextInput.emotion";
import type { AuthFlowProps } from "./AuthTypes.emotion";

const buttonsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column-reverse",
  [cssMediaQueryMinWidthTablet]: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  gap: spacing.ms,
  justifyContent: "flex-end",
};
const justOneStyle: Interpolation = {
  [cssMediaQueryMinWidthTablet]: { justifyContent: "flex-end" },
};
const inputFormFieldStyle: Interpolation = {
  width: spacing.full,
};
export const EmailAuthFlow = ({
  setFlow,
  email,
  state,
  setState,
  allowedMethods,
  customSchema,
  layoutCSS = {},
  hideHeader,
  className = "",
}: AuthFlowProps) => {
  const t = useTranslations();
  const alert = useAlert();
  const { mutationEmailExists } = useRequestAuth();
  const { mutationPost: sendOtp } = useRequestOtp();
  const { query: queryGeolocation } = useRequestGeolocation();
  const isDesktop = useIsDesktopViewport();

  const onSubmit = async ({ email }: EmailAuthFlowForm) => {
    try {
      const exists = await mutationEmailExists.mutateAsync({ email });
      setState({ ...state, email, ...exists });

      /** MAGIC */
      if (exists.use_magic_link_flow) {
        await sendOtp.mutateAsync({ email });
        setState({ ...state, phone: undefined, email, exists });
        setFlow("otp");

        /** Allow Password signups IF NOT in current A/B magic-link treatment */
      } else setFlow("passwordWithSignup");
    } catch (error) {
      await alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
    }
  };
  const { resolver, errors, errorsCSS, fieldsCSS, inputProps } =
    useValidator<EmailAuthFlowForm>(
      standardSchemaResolver(customSchema ?? emailAuthFlowSchema),
    );
  const form = useForm({ resolver });

  useViewedScreen({ screen: "email_input" });

  return (
    <AuthForm<EmailAuthFlowForm>
      form={form}
      defaultValues={{ email }}
      onSubmit={onSubmit}
      className={className}
      layoutCSS={layoutCSS}
      footer={(_, disabled) => (
        <div
          key={"buttons"}
          css={[
            buttonsStyle,
            (queryGeolocation.data?.country !== "US" ||
              !allowedMethods.includes("phone")) &&
              justOneStyle,
          ]}
        >
          {queryGeolocation.data?.country === "US" &&
            allowedMethods.includes("phone") && (
              <Button
                type="button"
                size="m"
                variant="transparentPrimary"
                onClick={() => setFlow("phone")}
                isFullWidth={!isDesktop}
              >
                {t("sms_sign_up_phone_instead")}
              </Button>
            )}
          <AuthSubmitButton
            disabled={disabled}
            data-testid={HallowDataTestId.AuthContinueButton}
          >
            {t("onboarding_button_continue")}
          </AuthSubmitButton>
        </div>
      )}
    >
      {(form) => (
        <>
          {!hideHeader && <EmailHeader onBack={() => setFlow("main")} />}
          <Text type="headline" size="m" as="h2">
            {t("email_input_headline")}
          </Text>
          <FormField
            control={form.control}
            name="email"
            showErrorMessage={false}
            defaultValue={""}
            render={({ field }) => (
              <div>
                <AuthTextInput
                  type="email"
                  required
                  autoFocus
                  autoComplete="username"
                  autoCorrect="off"
                  placeholder={t("email_input_hint")}
                  css={fieldsCSS[field.name]}
                  {...inputProps}
                  {...field}
                />
                <AuthError css={errorsCSS[field.name]}>
                  {errors?.[field.name]?.message
                    ? t(errors[field.name].message)
                    : ""}
                </AuthError>
              </div>
            )}
            css={inputFormFieldStyle}
          />
        </>
      )}
    </AuthForm>
  );
};
